import type { EbookDocument, EbookQuestion, EbookWorkspace } from '@wx-weiqi/contracts';
import type { EbookClient } from './ebooks';

/** Chapter metadata is saved with the workspace under its existing optimistic version. */
export function newEbookChapterProblem(name: string, chapters: readonly string[]) {
  const normalized = name.trim();
  if (!normalized) return '请填写章节名称。';
  if (normalized.length > 120) return '章节名称不能超过 120 个字符，请缩短后再保存。';
  if (['未分章', '未分配章节'].includes(normalized))
    return '“未分章”是系统保留名称，请填写其他章节名称。';
  if (chapters.some((chapter) => chapter.trim() === normalized))
    return '本书已有同名章节，请从“选择章节”中选择，或换一个名称。';
  if (chapters.length >= 500) return '本书章节已达 500 个，暂时无法新增。';
  return '';
}

/** Validate a rename while allowing the chapter to keep its current name. */
export function ebookChapterRenameProblem(
  name: string,
  currentName: string,
  chapters: readonly string[],
) {
  const normalized = name.trim();
  const current = currentName.trim();
  if (!normalized) return '请填写章节名称。';
  if (normalized.length > 120) return '章节名称不能超过 120 个字符，请缩短后再保存。';
  if (['未分章', '未分配章节'].includes(normalized))
    return '“未分章”是系统保留名称，请填写其他章节名称。';
  if (normalized !== current && chapters.some((chapter) => chapter.trim() === normalized))
    return '本书已有同名章节，请换一个名称。';
  return '';
}

export function workspaceWithNewEbookChapter(
  workspace: EbookWorkspace,
  question: EbookQuestion,
  name: string,
  chapters: readonly string[],
): EbookWorkspace {
  const problem = newEbookChapterProblem(name, chapters);
  if (problem) throw new Error(problem);
  if (!workspace.questions.some((item) => item.id === question.id))
    throw new Error('当前题目不在本书草稿中，请重新打开题目后再新增章节。');
  const chapter = name.trim();
  return {
    ...workspace,
    book: { ...workspace.book, chapters: [...chapters, chapter] },
    questions: workspace.questions.map((item) =>
      item.id === question.id ? { ...question, chapter } : item,
    ),
  };
}

/** Rename a shared chapter and update every question that references it. */
export function workspaceWithRenamedEbookChapter(
  workspace: EbookWorkspace,
  currentName: string,
  nextName: string,
  chapters: readonly string[],
): EbookWorkspace {
  const current = currentName.trim();
  const normalized = nextName.trim();
  const existingChapters = workspace.book.chapters ?? [];
  const problem = ebookChapterRenameProblem(normalized, current, chapters);
  if (problem) throw new Error(problem);
  if (!current || !chapters.some((chapter) => chapter.trim() === current))
    throw new Error('当前章节不存在，请刷新教材后再修改。');
  if (!existingChapters.some((chapter) => chapter.trim() === current))
    throw new Error('当前章节不在教材草稿中，请刷新教材后再修改。');
  return {
    ...workspace,
    book: {
      ...workspace.book,
      chapters: existingChapters.map((chapter) =>
        chapter.trim() === current ? normalized : chapter,
      ),
    },
    questions: workspace.questions.map((question) =>
      question.chapter.trim() === current ? { ...question, chapter: normalized } : question,
    ),
  };
}

export async function saveEbookChapterWorkspace(
  client: Pick<EbookClient, 'save' | 'document'>,
  document: Pick<EbookDocument, 'id' | 'stateVersion'>,
  workspace: EbookWorkspace,
) {
  // A rejected write (including a version conflict) is not retried or merged silently.
  await client.save(document.id, document.stateVersion, workspace);
  try {
    return { document: await client.document(document.id), workspace };
  } catch {
    // A confirmed write is still successful if the follow-up read fails. The caller
    // must retain workspace and block old-version mutations until a fresh read succeeds.
    return { document: null, workspace };
  }
}
